import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  getGuestInfoByToken,
  submitGuestInfo,
  type GuestInfoSubmitInput,
} from "@/lib/guest-info.functions";

export const Route = createFileRoute("/guest-info/$token")({
  head: () => ({ meta: [{ title: "Guest information, Casa Mi Pajobe" }, { name: "robots", content: "noindex" }] }),
  component: GuestInfoPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="mx-auto max-w-xl px-6 py-24 text-center">
        <h1 className="text-2xl font-semibold">Something went wrong</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
        <button
          onClick={() => { router.invalidate(); reset(); }}
          className="mt-6 rounded-full bg-primary px-5 py-2 text-sm text-primary-foreground"
        >
          Try again
        </button>
      </div>
    );
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-xl px-6 py-24 text-center text-muted-foreground">
      Form not found.
    </div>
  ),
});

type FormLang = "en" | "nl" | "fr" | "de";

const T: Record<FormLang, {
  title: string; intro: string; stay: string; renter: string; guests: string; travel: string;
  bedroomsLabel: string; babyKit: string; foundVia: string; submit: string; saved: string; updateAgain: string;
  langLabel: string;
  checkInOut: string;
  earlyCheckIn: string;
  lateCheckOut: string;
  checkInOutHint: string;
  stayLabels: { reference: string; checkIn: string; checkOut: string; nights: string; adults: string; children: string; babies: string };
  fields: {
    familyName: string; firstName: string; address: string; postcode: string; city: string; country: string;
    email: string; phone: string;
    gender: string; nationality: string; dob: string; passport: string; passportIssue: string; passportExpiry: string;
    docType: string; docTypePassport: string; docTypeId: string;
    addPerson: string; baby: string; remove: string;
    travelMode: string; car: string; plane: string;
    date: string; airport: string; flight: string; departure: string; arrival: string; addFlight: string;
    iban: string; ibanHint: string;
  };
  errorNotFound: string;
  errorGeneric: string;
}> = {
  en: {
    title: "Guest information",
    intro: "Please fill in the details below. These are required by the local rental registration office and our insurance. Only the people on this list are allowed to use the villa and pool.",
    stay: "Your stay", renter: "Main contact", guests: "Guests staying at the villa",
    travel: "Travel details (if known)",
    bedroomsLabel: "Bedrooms requested", babyKit: "Baby kit (travel cot + high chair, 0-24 months), €30 per stay",
    foundVia: "How did you hear about Casa Mi Pajobe?",
    submit: "Submit information", saved: "Thank you! Your information has been saved.", updateAgain: "Update again",
    langLabel: "Language",
    checkInOut: "Check-in / check-out options",
    earlyCheckIn: "Request early check-in",
    lateCheckOut: "Request late check-out",
    checkInOutHint: "To be arranged and paid directly with the key keeper, subject to availability.",
    stayLabels: { reference: "Reference", checkIn: "Check-in", checkOut: "Check-out", nights: "Nights", adults: "Adults", children: "Children", babies: "Babies" },
    fields: {
      familyName: "Family name", firstName: "First name", address: "Address", postcode: "Postcode", city: "City", country: "Country",
      email: "Email", phone: "Phone",
      gender: "Gender", nationality: "Nationality", dob: "Date of birth", passport: "Document number", passportIssue: "Issue date", passportExpiry: "Expiry date",
      docType: "Document type", docTypePassport: "Passport", docTypeId: "ID card",
      addPerson: "+ Add guest", baby: "Baby", remove: "Remove",
      travelMode: "Arriving by", car: "Car", plane: "Plane",
      date: "Date", airport: "Airport", flight: "Flight no.", departure: "Departure", arrival: "Arrival", addFlight: "+ Add flight",
      iban: "IBAN (for security deposit refund)",
      ibanHint: "Used only to refund the refundable security deposit after your stay.",
    },
    errorNotFound: "This form link is invalid or no longer exists.",
    errorGeneric: "Could not save. Please try again.",
  },
  nl: {
    title: "Inlichtingenfiche",
    intro: "Vul hieronder de gegevens in. Deze worden gevraagd door het verhuurregistratiekantoor en de verzekering. Enkel de personen op deze lijst mogen de woning en het zwembad gebruiken.",
    stay: "Uw verblijf", renter: "Hoofdcontact", guests: "Personen die in de woning verblijven",
    travel: "Reisgegevens (indien gekend)",
    bedroomsLabel: "Aantal slaapkamers", babyKit: "Kinderpakket (reisbedje + kinderstoel, 0-24 maanden), €30 per verblijf",
    foundVia: "Hoe heeft u Casa Mi Pajobe gevonden?",
    submit: "Gegevens verzenden", saved: "Bedankt! Uw gegevens zijn opgeslagen.", updateAgain: "Opnieuw bewerken",
    langLabel: "Taal",
    checkInOut: "Vroege check-in / late check-out",
    earlyCheckIn: "Vroege check-in aanvragen",
    lateCheckOut: "Late check-out aanvragen",
    checkInOutHint: "Te vereffenen en rechtstreeks te betalen aan de sleutelbeheerder, onder voorbehoud van beschikbaarheid.",
    stayLabels: { reference: "Referentie", checkIn: "Aankomst", checkOut: "Vertrek", nights: "Nachten", adults: "Volwassenen", children: "Kinderen", babies: "Baby's" },
    fields: {
      familyName: "Familienaam", firstName: "Voornaam", address: "Adres", postcode: "Postcode", city: "Stad", country: "Land",
      email: "E-mail", phone: "Telefoon",
      gender: "Geslacht", nationality: "Nationaliteit", dob: "Geboortedatum", passport: "Documentnummer", passportIssue: "Uitgiftedatum", passportExpiry: "Vervaldatum",
      docType: "Documenttype", docTypePassport: "Paspoort", docTypeId: "Identiteitskaart",
      addPerson: "+ Persoon toevoegen", baby: "Baby", remove: "Verwijderen",
      travelMode: "Aankomst per", car: "Auto", plane: "Vliegtuig",
      date: "Datum", airport: "Luchthaven", flight: "Vluchtnummer", departure: "Vertrekuur", arrival: "Aankomstuur", addFlight: "+ Vlucht toevoegen",
      iban: "IBAN (voor terugbetaling waarborg)",
      ibanHint: "Wordt enkel gebruikt om de terugbetaalbare waarborg na uw verblijf terug te storten.",
    },
    errorNotFound: "Deze link is ongeldig of bestaat niet meer.",
    errorGeneric: "Opslaan mislukt. Probeer opnieuw.",
  },
  fr: {
    title: "Fiche d'informations",
    intro: "Merci de compléter les informations ci-dessous. Elles sont demandées par le bureau d'enregistrement des locations et notre assurance. Seules les personnes inscrites peuvent utiliser la villa et la piscine.",
    stay: "Votre séjour", renter: "Contact principal", guests: "Personnes séjournant dans la villa",
    travel: "Informations de voyage (si connues)",
    bedroomsLabel: "Chambres souhaitées", babyKit: "Kit bébé (lit parapluie + chaise haute, 0-24 mois), 30 € par séjour",
    foundVia: "Comment avez-vous découvert Casa Mi Pajobe ?",
    submit: "Envoyer", saved: "Merci ! Vos informations sont enregistrées.", updateAgain: "Modifier à nouveau",
    langLabel: "Langue",
    checkInOut: "Arrivée anticipée / départ tardif",
    earlyCheckIn: "Demander une arrivée anticipée",
    lateCheckOut: "Demander un départ tardif",
    checkInOutHint: "À régler et à payer directement auprès du gardien des clés, sous réserve de disponibilité.",
    stayLabels: { reference: "Référence", checkIn: "Arrivée", checkOut: "Départ", nights: "Nuits", adults: "Adultes", children: "Enfants", babies: "Bébés" },
    fields: {
      familyName: "Nom de famille", firstName: "Prénom", address: "Adresse", postcode: "Code postal", city: "Ville", country: "Pays",
      email: "E-mail", phone: "Téléphone",
      gender: "Sexe", nationality: "Nationalité", dob: "Date de naissance", passport: "N° du document", passportIssue: "Date d'émission", passportExpiry: "Date d'expiration",
      docType: "Type de document", docTypePassport: "Passeport", docTypeId: "Carte d'identité",
      addPerson: "+ Ajouter une personne", baby: "Bébé", remove: "Supprimer",
      travelMode: "Arrivée en", car: "Voiture", plane: "Avion",
      date: "Date", airport: "Aéroport", flight: "N° de vol", departure: "Départ", arrival: "Arrivée", addFlight: "+ Ajouter un vol",
      iban: "IBAN (pour le remboursement de la caution)",
      ibanHint: "Utilisé uniquement pour rembourser la caution remboursable après votre séjour.",
    },
    errorNotFound: "Ce lien est invalide ou n'existe plus.",
    errorGeneric: "Échec de l'enregistrement. Veuillez réessayer.",
  },
  de: {
    title: "Gästeinformationsformular",
    intro: "Bitte füllen Sie die folgenden Angaben aus. Sie werden vom Vermietungsregistrierungsbüro und unserer Versicherung verlangt. Nur die hier aufgeführten Personen dürfen Villa und Pool nutzen.",
    stay: "Ihr Aufenthalt", renter: "Hauptkontakt", guests: "Personen, die in der Villa wohnen",
    travel: "Reisedaten (falls bekannt)",
    bedroomsLabel: "Gewünschte Schlafzimmer", babyKit: "Babyausstattung (Reisebett + Hochstuhl, 0-24 Monate), 30 € pro Aufenthalt",
    foundVia: "Wie haben Sie von Casa Mi Pajobe erfahren?",
    submit: "Daten senden", saved: "Danke! Ihre Daten wurden gespeichert.", updateAgain: "Erneut bearbeiten",
    langLabel: "Sprache",
    checkInOut: "Frühes Check-in / spätes Check-out",
    earlyCheckIn: "Frühes Check-in anfragen",
    lateCheckOut: "Spätes Check-out anfragen",
    checkInOutHint: "Direkt mit dem Schlüsselverwalter zu vereinbaren und zu bezahlen, je nach Verfügbarkeit.",
    stayLabels: { reference: "Referenz", checkIn: "Anreise", checkOut: "Abreise", nights: "Nächte", adults: "Erwachsene", children: "Kinder", babies: "Babys" },
    fields: {
      familyName: "Familienname", firstName: "Vorname", address: "Adresse", postcode: "PLZ", city: "Stadt", country: "Land",
      email: "E-Mail", phone: "Telefon",
      gender: "Geschlecht", nationality: "Nationalität", dob: "Geburtsdatum", passport: "Dokumentnummer", passportIssue: "Ausstellungsdatum", passportExpiry: "Ablaufdatum",
      docType: "Dokumenttyp", docTypePassport: "Reisepass", docTypeId: "Personalausweis",
      addPerson: "+ Person hinzufügen", baby: "Baby", remove: "Entfernen",
      travelMode: "Anreise mit", car: "Auto", plane: "Flugzeug",
      date: "Datum", airport: "Flughafen", flight: "Flugnummer", departure: "Abflug", arrival: "Ankunft", addFlight: "+ Flug hinzufügen",
      iban: "IBAN (für Rückerstattung der Kaution)",
      ibanHint: "Wird nur zur Rückerstattung der erstattungsfähigen Kaution nach Ihrem Aufenthalt verwendet.",
    },
    errorNotFound: "Dieser Link ist ungültig oder existiert nicht mehr.",
    errorGeneric: "Speichern fehlgeschlagen. Bitte erneut versuchen.",
  },
};

type PersonRow = GuestInfoSubmitInput["persons"][number];
type TravelRow = GuestInfoSubmitInput["travel_info"][number];

function emptyPerson(position: number, is_baby = false): PersonRow {
  return {
    position, is_baby,
    gender: "", family_name: "", first_name: "", nationality: "",
    date_of_birth: "", document_number: "", document_issue_date: "", document_expiry_date: "", document_type: "",
  };
}

function emptyTravel(): TravelRow {
  return { date: "", airport: "", flight_number: "", departure_time: "", arrival_time: "" };
}

import { splitFullName as splitName } from "@/lib/name-utils";

function nightsBetween(a?: string | null, b?: string | null): number | null {
  if (!a || !b) return null;
  const ms = new Date(b).getTime() - new Date(a).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  return Math.round(ms / 86400000);
}

function GuestInfoPage() {
  const { token } = Route.useParams();
  const fetchFn = useServerFn(getGuestInfoByToken);
  const submitFn = useServerFn(submitGuestInfo);

  const [lang, setLang] = useState<FormLang>("en");
  useEffect(() => {
    // Priority: ?lang= query (from the email link) > navigator language.
    if (typeof window !== "undefined") {
      const q = new URLSearchParams(window.location.search).get("lang")?.toLowerCase();
      if (q === "nl" || q === "fr" || q === "de" || q === "en") {
        setLang(q as FormLang);
        return;
      }
    }
    if (typeof navigator !== "undefined") {
      const n = navigator.language.slice(0, 2).toLowerCase();
      if (n === "nl" || n === "fr" || n === "de") setLang(n as FormLang);
    }
  }, []);
  const t = T[lang];

  const q = useQuery({
    queryKey: ["guest-info", token],
    queryFn: () => fetchFn({ data: { token } }),
  });

  const [form, setForm] = useState<GuestInfoSubmitInput | null>(null);
  const [savedOnce, setSavedOnce] = useState(false);

  useEffect(() => {
    if (!q.data || form) return;
    if (!q.data.ok) return;
    const s = q.data.submission;
    const booking = q.data.booking;
    const persons: PersonRow[] = s.persons.length > 0
      ? s.persons.map((p, i) => ({
          position: p.position ?? i + 1,
          is_baby: !!p.is_baby,
          gender: p.gender ?? "",
          family_name: p.family_name ?? "",
          first_name: p.first_name ?? "",
          nationality: p.nationality ?? "",
          date_of_birth: (p.date_of_birth as string | null) ?? "",
          document_number: p.document_number ?? "",
          document_issue_date: (p.document_issue_date as string | null) ?? "",
          document_expiry_date: (p.document_expiry_date as string | null) ?? "",
          document_type: (p.document_type as "passport" | "id" | null) ?? "",
        }))
      : (() => {
          const adults = booking?.adults ?? 1;
          const kids = booking?.children ?? 0;
          const babies = booking?.babies ?? 0;
          const list: PersonRow[] = [];
          let pos = 1;
          for (let i = 0; i < adults + kids; i++) {
            const person = emptyPerson(pos++);
            if (i === 0 && booking?.name) {
              const { first, family } = splitName(booking.name);
              person.first_name = first;
              person.family_name = family;
              if (booking.country) person.nationality = booking.country;
            }
            list.push(person);
          }
          for (let i = 0; i < babies; i++) list.push(emptyPerson(pos++, true));
          if (list.length === 0) list.push(emptyPerson(1));
          return list;
        })();

    setForm({
      token,
      renter_family_name:
        s.renter_family_name ?? (booking?.name ? splitName(booking.name).family : ""),
      renter_first_name:
        s.renter_first_name ?? (booking?.name ? splitName(booking.name).first : ""),
      renter_address: s.renter_address ?? "",
      renter_postcode: s.renter_postcode ?? "",
      renter_city: s.renter_city ?? "",
      renter_country: s.renter_country ?? booking?.country ?? "",
      renter_email: s.renter_email ?? booking?.email ?? "",
      renter_phone: s.renter_phone ?? booking?.phone ?? "",
      renter_iban: (s as { renter_iban?: string | null }).renter_iban ?? "",
      bedrooms_requested: s.bedrooms_requested ?? booking?.bedrooms ?? null,
      baby_kit: !!s.baby_kit,
      travel_mode: (s.travel_mode as "" | "car" | "plane" | null) ?? "",
      travel_info: Array.isArray(s.travel_info) && s.travel_info.length > 0
        ? (s.travel_info as TravelRow[])
        : [emptyTravel()],
      found_via: s.found_via ?? "",
      early_check_in: !!(s as { early_check_in?: boolean }).early_check_in,
      late_check_out: !!(s as { late_check_out?: boolean }).late_check_out,
      persons,
    });
    if (s.submitted_at) setSavedOnce(true);
  }, [q.data, form, token]);

  const mutation = useMutation({
    mutationFn: (payload: GuestInfoSubmitInput) => submitFn({ data: payload }),
    onSuccess: () => { setSavedOnce(true); window.scrollTo({ top: 0, behavior: "smooth" }); },
  });

  const booking = q.data?.ok ? q.data.booking : null;

  const bookingSummary = useMemo(() => {
    if (!booking) return null;
    return `${booking.reference} · ${booking.check_in} → ${booking.check_out}`;
  }, [booking]);

  if (q.isLoading) {
    return <div className="mx-auto max-w-xl px-6 py-24 text-center text-muted-foreground">Loading…</div>;
  }
  if (q.data && !q.data.ok) {
    return (
      <div className="mx-auto max-w-xl px-6 py-24 text-center">
        <h1 className="text-2xl font-semibold">{t.errorNotFound}</h1>
      </div>
    );
  }
  if (!form) return null;

  const update = (patch: Partial<GuestInfoSubmitInput>) => setForm({ ...form, ...patch });
  const updatePerson = (idx: number, patch: Partial<PersonRow>) => {
    const next = form.persons.slice();
    next[idx] = { ...next[idx], ...patch };
    update({ persons: next });
  };
  const updateTravel = (idx: number, patch: Partial<TravelRow>) => {
    const next = form.travel_info.slice();
    next[idx] = { ...next[idx], ...patch };
    update({ travel_info: next });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-6 py-10">
        <div className="mb-6 flex items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Casa Mi Pajobe</p>
            <h1 className="font-display text-3xl font-semibold">{t.title}</h1>
            {bookingSummary ? (
              <p className="mt-1 text-sm text-muted-foreground">{bookingSummary}</p>
            ) : null}
          </div>
          <label className="text-xs text-muted-foreground">
            {t.langLabel}{" "}
            <select
              value={lang}
              onChange={(e) => setLang(e.target.value as FormLang)}
              className="ml-1 rounded-md border border-border bg-card px-2 py-1 text-sm"
            >
              <option value="en">EN</option>
              <option value="nl">NL</option>
              <option value="fr">FR</option>
              <option value="de">DE</option>
            </select>
          </label>
        </div>

        {savedOnce && !mutation.isPending ? (
          <div className="mb-6 rounded-2xl border border-green-200 bg-green-50 p-5 text-sm text-green-900">
            {t.saved}
            <button
              onClick={() => setSavedOnce(false)}
              className="ml-3 underline underline-offset-2"
            >
              {t.updateAgain}
            </button>
          </div>
        ) : (
          <p className="mb-6 rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground">
            {t.intro}
          </p>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate(form);
          }}
          className="space-y-8"
        >
          {/* Main contact */}
          <Fieldset legend={t.renter}>
            <Grid>
              <Field label={t.fields.familyName}><Input value={form.renter_family_name ?? ""} onChange={(v) => update({ renter_family_name: v })} /></Field>
              <Field label={t.fields.firstName}><Input value={form.renter_first_name ?? ""} onChange={(v) => update({ renter_first_name: v })} /></Field>
              <Field label={t.fields.address} full><Input value={form.renter_address ?? ""} onChange={(v) => update({ renter_address: v })} /></Field>
              <Field label={t.fields.postcode}><Input value={form.renter_postcode ?? ""} onChange={(v) => update({ renter_postcode: v })} /></Field>
              <Field label={t.fields.city}><Input value={form.renter_city ?? ""} onChange={(v) => update({ renter_city: v })} /></Field>
              <Field label={t.fields.country}><Input value={form.renter_country ?? ""} onChange={(v) => update({ renter_country: v })} /></Field>
              <Field label={t.fields.email}><Input type="email" value={form.renter_email ?? ""} onChange={(v) => update({ renter_email: v })} /></Field>
              <Field label={t.fields.phone}><Input value={form.renter_phone ?? ""} onChange={(v) => update({ renter_phone: v })} /></Field>
              <Field label={t.fields.iban} full>
                <Input value={form.renter_iban ?? ""} onChange={(v) => update({ renter_iban: v.toUpperCase() })} placeholder="BE68 5390 0754 7034" />
                <p className="mt-1 text-[11px] text-muted-foreground">{t.fields.ibanHint}</p>
              </Field>
            </Grid>
          </Fieldset>

          {/* Stay */}
          <Fieldset legend={t.stay}>
            {booking ? (
              <dl className="mb-4 grid grid-cols-2 gap-x-4 gap-y-2 rounded-xl border border-border bg-muted/40 p-4 text-sm sm:grid-cols-3">
                <div>
                  <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t.stayLabels.reference}</dt>
                  <dd className="font-medium">{booking.reference}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t.stayLabels.checkIn}</dt>
                  <dd className="font-medium">{booking.check_in}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t.stayLabels.checkOut}</dt>
                  <dd className="font-medium">{booking.check_out}</dd>
                </div>
                {(() => { const n = nightsBetween(booking.check_in, booking.check_out); return n ? (
                  <div>
                    <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t.stayLabels.nights}</dt>
                    <dd className="font-medium">{n}</dd>
                  </div>
                ) : null; })()}
                <div>
                  <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t.stayLabels.adults}</dt>
                  <dd className="font-medium">{booking.adults}</dd>
                </div>
                {booking.children > 0 ? (
                  <div>
                    <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t.stayLabels.children}</dt>
                    <dd className="font-medium">{booking.children}</dd>
                  </div>
                ) : null}
                {booking.babies > 0 ? (
                  <div>
                    <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t.stayLabels.babies}</dt>
                    <dd className="font-medium">{booking.babies}</dd>
                  </div>
                ) : null}
              </dl>
            ) : null}
            <Grid>
              <Field label={t.bedroomsLabel}>
                <select
                  value={form.bedrooms_requested ?? ""}
                  onChange={(e) => update({ bedrooms_requested: e.target.value ? Number(e.target.value) : null })}
                  className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm"
                >
                  <option value="">-</option>
                  {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </Field>
              <Field label={t.babyKit} full>
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.baby_kit} onChange={(e) => update({ baby_kit: e.target.checked })} />
                  <span>{t.babyKit}</span>
                </label>
              </Field>
            </Grid>
          </Fieldset>

          {/* Early check-in / late check-out */}
          <Fieldset legend={t.checkInOut}>
            <div className="space-y-2 text-sm">
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!form.early_check_in}
                  onChange={(e) => update({ early_check_in: e.target.checked })}
                />
                <span>{t.earlyCheckIn}</span>
              </label>
              <br />
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!form.late_check_out}
                  onChange={(e) => update({ late_check_out: e.target.checked })}
                />
                <span>{t.lateCheckOut}</span>
              </label>
              <p className="text-xs text-muted-foreground">{t.checkInOutHint}</p>
            </div>
          </Fieldset>

          {/* Guests */}
          <Fieldset legend={t.guests}>
            <div className="space-y-3">
              {form.persons.map((p, i) => (
                <div key={i} className="rounded-xl border border-border bg-card p-4">
                  <div className="mb-3 flex items-center justify-between text-xs uppercase tracking-wider text-muted-foreground">
                    <span>{p.is_baby ? t.fields.baby : `#${i + 1}`}</span>
                    <button
                      type="button"
                      onClick={() => update({ persons: form.persons.filter((_, j) => j !== i).map((x, j) => ({ ...x, position: j + 1 })) })}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      {t.fields.remove}
                    </button>
                  </div>
                  <Grid>
                    <Field label={t.fields.gender}>
                      <select
                        value={p.gender ?? ""}
                        onChange={(e) => updatePerson(i, { gender: e.target.value })}
                        className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm"
                      >
                        <option value="">-</option>
                        <option value="M">M</option>
                        <option value="F">F</option>
                        <option value="X">X</option>
                      </select>
                    </Field>
                    <Field label={t.fields.nationality}><Input value={p.nationality ?? ""} onChange={(v) => updatePerson(i, { nationality: v })} /></Field>
                    <Field label={t.fields.familyName}><Input value={p.family_name ?? ""} onChange={(v) => updatePerson(i, { family_name: v })} /></Field>
                    <Field label={t.fields.firstName}><Input value={p.first_name ?? ""} onChange={(v) => updatePerson(i, { first_name: v })} /></Field>
                    <Field label={t.fields.dob}><Input type="date" value={p.date_of_birth ?? ""} onChange={(v) => updatePerson(i, { date_of_birth: v })} /></Field>
                    {!p.is_baby ? (
                      <>
                        <Field label={t.fields.docType}>
                          <select
                            value={p.document_type ?? ""}
                            onChange={(e) => updatePerson(i, { document_type: e.target.value as "passport" | "id" | "" })}
                            className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm"
                          >
                            <option value="">-</option>
                            <option value="passport">{t.fields.docTypePassport}</option>
                            <option value="id">{t.fields.docTypeId}</option>
                          </select>
                        </Field>
                        <Field label={t.fields.passport}><Input value={p.document_number ?? ""} onChange={(v) => updatePerson(i, { document_number: v })} /></Field>
                        <Field label={t.fields.passportIssue}><Input type="date" value={p.document_issue_date ?? ""} onChange={(v) => updatePerson(i, { document_issue_date: v })} /></Field>
                        <Field label={t.fields.passportExpiry}><Input type="date" value={p.document_expiry_date ?? ""} onChange={(v) => updatePerson(i, { document_expiry_date: v })} /></Field>
                      </>
                    ) : null}
                  </Grid>
                </div>
              ))}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => update({ persons: [...form.persons, emptyPerson(form.persons.length + 1)] })}
                  className="rounded-full border border-border px-4 py-1.5 text-sm hover:bg-muted"
                  disabled={form.persons.length >= 10}
                >
                  {t.fields.addPerson}
                </button>
                <button
                  type="button"
                  onClick={() => update({ persons: [...form.persons, emptyPerson(form.persons.length + 1, true)] })}
                  className="rounded-full border border-border px-4 py-1.5 text-sm hover:bg-muted"
                  disabled={form.persons.length >= 10}
                >
                  + {t.fields.baby}
                </button>
              </div>
            </div>
          </Fieldset>

          {/* Travel */}
          <Fieldset legend={t.travel}>
            <div className="mb-3 flex gap-4 text-sm">
              {(["car", "plane"] as const).map((m) => (
                <label key={m} className="inline-flex items-center gap-2">
                  <input
                    type="radio"
                    checked={form.travel_mode === m}
                    onChange={() => update({ travel_mode: m })}
                  />
                  <span>{m === "car" ? t.fields.car : t.fields.plane}</span>
                </label>
              ))}
            </div>
            {form.travel_mode === "plane" ? (
              <div className="space-y-3">
                {form.travel_info.map((row, i) => (
                  <div key={i} className="rounded-xl border border-border bg-card p-4">
                    <div className="mb-2 flex justify-end">
                      <button
                        type="button"
                        onClick={() => update({ travel_info: form.travel_info.filter((_, j) => j !== i) })}
                        className="text-xs text-muted-foreground hover:text-foreground"
                      >
                        {t.fields.remove}
                      </button>
                    </div>
                    <Grid>
                      <Field label={t.fields.date}><Input type="date" value={row.date} onChange={(v) => updateTravel(i, { date: v })} /></Field>
                      <Field label={t.fields.airport}><Input value={row.airport} onChange={(v) => updateTravel(i, { airport: v })} /></Field>
                      <Field label={t.fields.flight}><Input value={row.flight_number} onChange={(v) => updateTravel(i, { flight_number: v })} /></Field>
                      <Field label={t.fields.departure}><Input value={row.departure_time} onChange={(v) => updateTravel(i, { departure_time: v })} /></Field>
                      <Field label={t.fields.arrival}><Input value={row.arrival_time} onChange={(v) => updateTravel(i, { arrival_time: v })} /></Field>
                    </Grid>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => update({ travel_info: [...form.travel_info, emptyTravel()] })}
                  className="rounded-full border border-border px-4 py-1.5 text-sm hover:bg-muted"
                  disabled={form.travel_info.length >= 10}
                >
                  {t.fields.addFlight}
                </button>
              </div>
            ) : null}
          </Fieldset>

          {/* Found via */}
          <Fieldset legend={t.foundVia}>
            <Input value={form.found_via ?? ""} onChange={(v) => update({ found_via: v })} />
          </Fieldset>

          {mutation.isError ? (
            <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {t.errorGeneric}
            </p>
          ) : null}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={mutation.isPending}
              className="rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground disabled:opacity-60"
            >
              {mutation.isPending ? "…" : t.submit}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Fieldset({ legend, children }: { legend: string; children: React.ReactNode }) {
  return (
    <fieldset className="rounded-2xl border border-border bg-card p-5">
      <legend className="px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{legend}</legend>
      <div className="mt-2">{children}</div>
    </fieldset>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>;
}

function Field({ label, full, children }: { label: string; full?: boolean; children: React.ReactNode }) {
  return (
    <label className={`block text-xs ${full ? "sm:col-span-2" : ""}`}>
      <span className="mb-1 block font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function Input({ value, onChange, type = "text", placeholder }: { value: string; onChange: (v: string) => void; type?: string; placeholder?: string }) {
  return (
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm"
    />
  );
}